let CATALOG = [];
let VEHICLES = [];
let VEHICLE_OPTIONS = [];
const GARAGE_CONFIG = window.BRACE1_GARAGE || {};
const STORE_URL = GARAGE_CONFIG.storeUrl || "";
const CATALOG_URL = GARAGE_CONFIG.catalogUrl || "./catalog.json";
const MARKETPLACE_CATALOG_URL = GARAGE_CONFIG.marketplaceCatalogUrl || "";
const VEHICLE_OPTIONS_URL = GARAGE_CONFIG.vehicleOptionsUrl || "";
const EMBEDDED = Boolean(GARAGE_CONFIG.embedded);
const CUSTOMER = GARAGE_CONFIG.customer || null;
const CLOUD_SYNC_ENABLED = Boolean(GARAGE_CONFIG.syncEnabled);
const CLOUD_SYNC_PATH = GARAGE_CONFIG.syncPath || "/apps/brace1-garage/sync";
const BUNDLE_CODE = String(GARAGE_CONFIG.bundleCode || "BUILD10").trim().toUpperCase();
const BUNDLE_PERCENT = Math.max(0, Number(GARAGE_CONFIG.bundlePercent) || 10);
const BUNDLE_MIN_ITEMS = Math.max(2, Number(GARAGE_CONFIG.bundleMinItems) || 2);

const SLOT_LABELS = {
  strut: "Front Strut",
  mirrors: "Mirrors",
  shifter: "Shifter",
  paddles: "Paddles",
};

const SLOT_THUMB = {
  strut: { label: "Strut", color: "#7a1018" },
  mirrors: { label: "Mirror", color: "#1d2a3a" },
  shifter: { label: "Shift", color: "#11161d" },
  paddles: { label: "Paddle", color: "#172033" },
};

const PART_BLURBS = {
  strut: "Stiffens the front chassis for sharper turn-in.",
  mirrors: "Street-ready visibility upgrade with clean fitment.",
  shifter: "Cabin feel upgrade — quicker, more connected shifts.",
  paddles: "Race-inspired control at your fingertips.",
};

function decoratePart(part) {
  const slot = part.slot;
  return {
    ...part,
    blurb: PART_BLURBS[slot] || "",
    thumb: SLOT_THUMB[slot] || { label: "Part", color: "#1d2a3a" },
  };
}

function buildVehiclesFromCatalog(parts) {
  const map = new Map();
  parts.forEach((part) => {
    (part.vehicles || []).forEach((v) => {
      const key = `${v.make}|${v.model}`;
      if (!map.has(key)) {
        map.set(key, {
          make: v.make,
          model: v.model,
          yearStart: v.yearStart,
          yearEnd: v.yearEnd,
          title: `Build your ${v.model}`,
          code: `B1-${String(v.model).replace(/\s+/g, "").toUpperCase().slice(0, 10)}`,
        });
      } else {
        const row = map.get(key);
        row.yearStart = Math.min(row.yearStart, v.yearStart);
        row.yearEnd = Math.max(row.yearEnd, v.yearEnd);
      }
    });
  });
  return Array.from(map.values()).sort((a, b) =>
    `${a.make}${a.model}`.localeCompare(`${b.make}${b.model}`)
  );
}

function partsForSelection(year, make, model, trim, engine) {
  const y = Number(year);
  if (!y || !make || !model) return [];
  const seen = new Set();
  const rows = [];
  const trimKey = String(trim || "").trim();
  const engineKey = String(engine || "").trim();
  CATALOG.forEach((part) => {
    const fits = (part.vehicles || []).some((v) => {
      if (v.make !== make || v.model !== model) return false;
      if (y < v.yearStart || y > v.yearEnd) return false;
      /* Optional trim/engine on catalog entries — skip check when absent */
      if (Array.isArray(v.trims) && v.trims.length && trimKey) {
        if (!v.trims.includes(trimKey)) return false;
      }
      if (Array.isArray(v.engines) && v.engines.length && engineKey) {
        if (!v.engines.includes(engineKey)) return false;
      }
      return true;
    });
    if (!fits || seen.has(part.id)) return;
    seen.add(part.id);
    rows.push(decoratePart({ ...part, slot: part.slot }));
  });
  return rows;
}

async function loadCatalog() {
  const res = await fetch(CATALOG_URL, { cache: "no-store" });
  if (!res.ok) throw new Error("Could not load catalog");
  const data = await res.json();
  const firstParty = (data.parts || []).map((p) => ({
    ...p,
    source: p.source || "brace1",
  }));

  let marketplace = [];
  if (MARKETPLACE_CATALOG_URL) {
    try {
      const mpRes = await fetch(MARKETPLACE_CATALOG_URL, {
        cache: "default",
      });
      if (mpRes.ok) {
        const mpData = await mpRes.json();
        marketplace = (mpData.parts || []).map((p) => ({
          ...p,
          source: p.source || "marketplace",
        }));
      }
    } catch (err) {
      console.warn("Brace1 marketplace catalog unavailable", err);
    }
  }

  // Merge: first-party Brace1 parts win on ID collision.
  const byId = new Map();
  marketplace.forEach((p) => byId.set(String(p.id), p));
  firstParty.forEach((p) => byId.set(String(p.id), p));
  CATALOG = Array.from(byId.values());
  VEHICLES = buildVehiclesFromCatalog(CATALOG);

  VEHICLE_OPTIONS = [];
  if (VEHICLE_OPTIONS_URL) {
    try {
      const optRes = await fetch(VEHICLE_OPTIONS_URL, { cache: "no-store" });
      if (optRes.ok) {
        const optData = await optRes.json();
        VEHICLE_OPTIONS = optData.vehicles || [];
      }
    } catch (err) {
      console.warn("Brace1 vehicle options unavailable", err);
    }
  }
}

const garageRoot = document.querySelector("[data-brace1-garage]");
const LOOKUP_MODE =
  GARAGE_CONFIG.mode === "lookup" ||
  garageRoot?.dataset.garageMode === "lookup";
const ACCOUNT_MODE =
  GARAGE_CONFIG.mode === "account" ||
  garageRoot?.dataset.garageMode === "account";

function byId(id) {
  return garageRoot ? garageRoot.querySelector(`#${id}`) : null;
}

const state = {
  year: "",
  make: "",
  model: "",
  trim: "",
  engine: "",
  activeSlot: "strut",
  purchased: {},
  owned: {},
};

const els = {
  partsList: byId("partsList"),
  panelEyebrow: byId("panelEyebrow"),
  panelTitle: byId("panelTitle"),
  panelHint: byId("panelHint"),
  buildList: byId("buildList"),
  buildTotal: byId("buildTotal"),
  buildCount: byId("buildCount"),
  buildFill: byId("buildFill"),
  buildNudge: byId("buildNudge"),
  buildNudgeFine: byId("buildNudgeFine"),
  perfScore: byId("perfScore"),
  buyBuild: byId("buyBuild"),
  clearBuild: byId("clearBuild"),
  yearSelect: byId("yearSelect"),
  makeSelect: byId("makeSelect"),
  modelSelect: byId("modelSelect"),
  trimSelect: byId("trimSelect"),
  engineSelect: byId("engineSelect"),
  stageTitle: byId("stageTitle"),
  bayCode: byId("bayCode"),
  toast: byId("toast"),
  profileOpen: byId("profileOpen"),
  profileClose: byId("profileClose"),
  profileOverlay: byId("profileOverlay"),
  profileForm: byId("profileForm"),
  profileName: byId("profileName"),
  profileEmail: byId("profileEmail"),
  profileGarage: byId("profileGarage"),
  profileAvatar: byId("profileAvatar"),
  profileChipName: byId("profileChipName"),
  profileChipGarage: byId("profileChipGarage"),
  savedBuilds: byId("savedBuilds"),
  saveBuildBtn: byId("saveBuildBtn"),
  stage: garageRoot ? garageRoot.querySelector(".stage") : null,
  hubAvatar: byId("hubAvatar"),
  hubName: byId("hubName"),
  hubGarage: byId("hubGarage"),
  hubVehicle: byId("hubVehicle"),
  hubProgress: byId("hubProgress"),
  hubOwned: byId("hubOwned"),
  hubPerf: byId("hubPerf"),
  hubMissing: byId("hubMissing"),
  ownedList: byId("ownedList"),
  hubNudge: byId("hubNudge"),
  hubContinue: byId("hubContinue"),
  ownedPanelList: byId("ownedPanelList"),
  ownedCount: byId("ownedCount"),
  slotRail: byId("slotRail"),
  lookupBuildBar: byId("lookupBuildBar"),
  lookupBuildCount: byId("lookupBuildCount"),
  lookupBuildTotal: byId("lookupBuildTotal"),
  lookupBuyBuild: byId("lookupBuyBuild"),
  lookupBody: byId("lookupBody"),
  lookupStage: byId("lookupStage"),
  lookupStageTitle: byId("lookupStageTitle"),
  lookupBayCode: byId("lookupBayCode"),
  lookupGarageLink: byId("lookupGarageLink"),
  lookupBuildGarageLink: byId("lookupBuildGarageLink"),
};

const PROFILE_KEY = "brace1-garage-profile-v1";
let cloudSyncTimer = null;
let cloudSyncInFlight = false;

function profileStorageKey() {
  if (CUSTOMER?.id) return `${PROFILE_KEY}-${CUSTOMER.id}`;
  return PROFILE_KEY;
}

function defaultProfile() {
  return {
    name: "Driver",
    email: "",
    garage: "My Garage",
    builds: [],
    vehicleBuilds: {},
    updatedAt: 0,
  };
}

function loadProfile() {
  try {
    const raw = localStorage.getItem(profileStorageKey());
    if (!raw) return defaultProfile();
    const parsed = JSON.parse(raw);
    return {
      ...defaultProfile(),
      ...parsed,
      vehicleBuilds: parsed.vehicleBuilds || {},
    };
  } catch (e) {
    return defaultProfile();
  }
}

function saveProfile(nextProfile) {
  localStorage.setItem(profileStorageKey(), JSON.stringify(nextProfile));
}

function touchProfile() {
  profile.updatedAt = Date.now();
  saveProfile(profile);
  scheduleCloudSync();
}

function mergeCloudGarage() {
  const cloud = GARAGE_CONFIG.cloudGarage;
  if (!CUSTOMER || !cloud || typeof cloud !== "object") return;

  const localUpdated = Number(profile.updatedAt || 0);
  const cloudUpdated = Number(cloud.updatedAt || 0);

  if (cloudUpdated > localUpdated) {
    profile = {
      ...profile,
      name: cloud.name || profile.name,
      garage: cloud.garage || profile.garage,
      builds: Array.isArray(cloud.builds) ? cloud.builds : profile.builds,
      vehicleBuilds: cloud.vehicleBuilds || profile.vehicleBuilds,
      updatedAt: cloudUpdated,
    };
    saveProfile(profile);
    return;
  }

  if (localUpdated > cloudUpdated) {
    scheduleCloudSync();
  }
}

function scheduleCloudSync() {
  if (!CUSTOMER || LOOKUP_MODE || !CLOUD_SYNC_ENABLED) return;
  clearTimeout(cloudSyncTimer);
  cloudSyncTimer = setTimeout(() => {
    syncGarageToCloud();
  }, 1500);
}

async function syncGarageToCloud() {
  if (!CUSTOMER || LOOKUP_MODE || !CLOUD_SYNC_ENABLED || cloudSyncInFlight) return;

  cloudSyncInFlight = true;
  try {
    const payload = {
      updatedAt: profile.updatedAt || Date.now(),
      vehicleBuilds: profile.vehicleBuilds || {},
      builds: profile.builds || [],
      garage: profile.garage || "My Garage",
      name: profile.name || "Driver",
    };

    const res = await fetch(CLOUD_SYNC_PATH, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (!res.ok) return;

    const saved = await res.json();
    if (saved && Number(saved.updatedAt) >= Number(payload.updatedAt)) {
      profile.updatedAt = Number(saved.updatedAt);
      saveProfile(profile);
    }
  } catch (err) {
    console.warn("Brace1 garage cloud sync failed", err);
  } finally {
    cloudSyncInFlight = false;
  }
}

let profile = loadProfile();

function syncProfileFromCustomer() {
  if (!CUSTOMER) return;
  profile.name = CUSTOMER.name || profile.name || "Driver";
  profile.email = CUSTOMER.email || profile.email || "";
  if (!profile.garage || profile.garage === "My Garage") {
    profile.garage = `${profile.name.split(" ")[0] || "My"}'s Garage`;
  }
}

function buildCatalogLookup() {
  const byProduct = new Map();
  const byVariant = new Map();
  CATALOG.forEach((part) => {
    byProduct.set(String(part.id), part);
    if (part.variantId) byVariant.set(String(part.variantId), part);
  });
  return { byProduct, byVariant };
}

function applyPurchasedFromAccount() {
  state.purchased = {};
  if (!CUSTOMER?.purchased?.length) return;

  const { byProduct, byVariant } = buildCatalogLookup();
  CUSTOMER.purchased.forEach((row) => {
    const raw =
      byVariant.get(String(row.variantId)) || byProduct.get(String(row.productId));
    if (!raw) return;
    const part = decoratePart({ ...raw, slot: raw.slot, purchased: true });
    const key = partKey(part);
    if (key) state.purchased[key] = part;
  });
}

function isPurchased(part) {
  const key = partKey(part);
  return Boolean(key && state.purchased[key]);
}

function currentVehicleBuildKey() {
  if (!state.make || !state.model) return null;
  return vehicleGroupKey(state.make, state.model);
}

function persistCurrentVehicleBuild() {
  const key = currentVehicleBuildKey();
  if (!key) return;

  if (!profile.vehicleBuilds) profile.vehicleBuilds = {};
  const owned = normalizeOwned(state.owned);

  if (!Object.keys(owned).length) {
    delete profile.vehicleBuilds[key];
  } else {
    profile.vehicleBuilds[key] = {
      year: state.year,
      make: state.make,
      model: state.model,
      trim: state.trim || "",
      engine: state.engine || "",
      owned,
    };
  }

  touchProfile();
}

function loadCurrentVehicleBuild() {
  const key = currentVehicleBuildKey();
  if (!key || !profile.vehicleBuilds?.[key]) {
    state.owned = {};
    return;
  }

  state.owned = normalizeOwned(profile.vehicleBuilds[key].owned || {});
}

function allManualOwnedMap() {
  const map = {};

  Object.values(profile.vehicleBuilds || {}).forEach((build) => {
    Object.entries(normalizeOwned(build.owned || {})).forEach(([key, part]) => {
      map[key] = part;
    });
  });

  Object.entries(state.owned).forEach(([key, part]) => {
    map[key] = part;
  });

  return map;
}

function purchasedForSelection() {
  if (!state.make || !state.model || !state.year) return [];
  const y = Number(state.year);
  return Object.values(state.purchased).filter((part) =>
    (part.vehicles || []).some(
      (v) =>
        v.make === state.make &&
        v.model === state.model &&
        y >= v.yearStart &&
        y <= v.yearEnd
    )
  );
}

function currentVehicleGarageParts() {
  return [...purchasedForSelection(), ...manualOwnedParts()];
}

function garageInventoryMap() {
  return { ...state.purchased, ...allManualOwnedMap() };
}

function garageParts() {
  return Object.values(garageInventoryMap());
}

function initials(name) {
  const parts = String(name || "DR")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "DR";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

function renderProfileChip() {
  if (!els.profileAvatar || !els.profileChipName || !els.profileChipGarage) return;
  els.profileAvatar.textContent = initials(profile.name);
  els.profileChipName.textContent = profile.name || "Driver";
  els.profileChipGarage.textContent = profile.garage || "My Garage";
}

function partKey(part) {
  return String(part?.id || part?.handle || "");
}

function normalizeOwned(raw) {
  if (!raw || typeof raw !== "object") return {};
  const entries = Object.entries(raw);
  if (!entries.length) return {};
  const slotKeys = new Set(["strut", "mirrors", "shifter", "paddles"]);
  if (slotKeys.has(entries[0][0])) {
    const out = {};
    entries.forEach(([, part]) => {
      const key = partKey(part);
      if (key) out[key] = part;
    });
    return out;
  }
  return raw;
}

function isOwned(part) {
  const key = partKey(part);
  return Boolean(key && (state.purchased[key] || state.owned[key]));
}

function ownedParts() {
  return garageParts();
}

function manualOwnedParts() {
  return Object.values(state.owned);
}

function vehicleGroupKey(make, model) {
  return `${make}|${model}`;
}

function formatYearRange(yearStart, yearEnd) {
  if (!yearStart) return "";
  if (!yearEnd || yearEnd === yearStart) return String(yearStart);
  return `${yearStart}\u2013${yearEnd}`;
}

function groupPartsByVehicle(parts) {
  const groups = new Map();
  const unassigned = [];

  parts.forEach((part) => {
    const vehicles = part.vehicles || [];
    if (!vehicles.length) {
      unassigned.push(part);
      return;
    }

    const primary = vehicles[0];
    const key = vehicleGroupKey(primary.make, primary.model);
    if (!groups.has(key)) {
      groups.set(key, {
        make: primary.make,
        model: primary.model,
        yearStart: primary.yearStart,
        yearEnd: primary.yearEnd,
        parts: [],
      });
    } else {
      const group = groups.get(key);
      group.yearStart = Math.min(group.yearStart, primary.yearStart);
      group.yearEnd = Math.max(group.yearEnd, primary.yearEnd);
    }
    groups.get(key).parts.push(part);
  });

  const rows = Array.from(groups.values()).sort((a, b) =>
    `${a.make}${a.model}`.localeCompare(`${b.make}${b.model}`)
  );

  if (unassigned.length) {
    rows.push({
      make: "",
      model: "",
      unassigned: true,
      parts: unassigned,
    });
  }

  return rows;
}

function vehicleGroupLabel(group) {
  if (group.unassigned) return "Other parts";
  return `${group.make} ${group.model}`;
}

function vehicleGroupMeta(group) {
  if (group.unassigned) {
    return `${group.parts.length} part${group.parts.length === 1 ? "" : "s"}`;
  }
  const years = formatYearRange(group.yearStart, group.yearEnd);
  const count = `${group.parts.length} part${group.parts.length === 1 ? "" : "s"}`;
  return years ? `${years} · ${count}` : count;
}

function isActiveVehicleGroup(group) {
  if (group.unassigned || !state.make || !state.model) return false;
  return state.make === group.make && state.model === group.model;
}

function selectVehicleGroup(group) {
  if (!group.make || !group.model || group.unassigned) return;

  const preferredYear =
    state.year &&
    Number(state.year) >= group.yearStart &&
    Number(state.year) <= group.yearEnd
      ? state.year
      : String(group.yearStart);

  /* Account hub: open full Garage with this car loaded */
  if (ACCOUNT_MODE || !els.yearSelect) {
    const url = new URL("/pages/garage", window.location.origin);
    url.searchParams.set("year", String(preferredYear));
    url.searchParams.set("make", group.make);
    url.searchParams.set("model", group.model);
    window.location.href = url.toString();
    return;
  }

  els.yearSelect.value = preferredYear;
  onYearChange();

  if ([...els.makeSelect.options].some((option) => option.value === group.make)) {
    els.makeSelect.value = group.make;
    onMakeChange();
    if ([...els.modelSelect.options].some((option) => option.value === group.model)) {
      els.modelSelect.value = group.model;
      onModelChange();
    }
  }

  showToast(`Loaded ${vehicleGroupLabel(group)}`);
}

function createOwnedSlot(part, index) {
  const bought = isPurchased(part);
  const btn = document.createElement("button");
  btn.type = "button";
  btn.className = "slot is-filled is-owned" + (bought ? " is-purchased" : "");
  btn.dataset.partId = partKey(part);
  btn.innerHTML = `
    <span class="slot__top">
      <span class="slot__tag">${String(index + 1).padStart(2, "0")}</span>
      <span class="slot__state">${bought ? "BOUGHT" : "OWNED"}</span>
    </span>
    <span class="slot__name">${part.name}</span>
    <span class="slot__value">${part.meta || SLOT_LABELS[part.slot] || ""}</span>
  `;
  btn.addEventListener("click", () => {
    if (ACCOUNT_MODE) {
      if (part.productUrl || part.url) {
        window.location.href = part.productUrl || part.url;
        return;
      }
      window.location.href = "/pages/garage";
      return;
    }
    state.activeSlot = part.slot;
    garageRoot.querySelectorAll(".slot-rail .slot").forEach((el) => el.classList.remove("is-active"));
    btn.classList.add("is-active");
    renderParts();
  });
  return btn;
}

function ownedInventory() {
  return garageParts();
}

function renderDriverHub() {
  if (!els.hubVehicle) return;
  const filled = ownedParts().length;
  const items = ownedParts();
  const perf = 100 + items.reduce((sum, p) => sum + (p.perf || 0), 0);
  const owned = ownedInventory();

  els.hubVehicle.textContent =
    state.year && state.make && state.model
      ? `${state.year} ${state.make} ${state.model}`
      : "Select a vehicle";
  els.hubProgress.textContent = String(filled);
  els.hubOwned.textContent = String(owned.length);
  els.hubPerf.textContent = String(perf);
  els.hubMissing.textContent = filled ? `${filled} in build` : "Empty";

  if (els.hubNudge) {
    if (!state.model) {
      els.hubNudge.textContent = "Select your vehicle to start a garage build.";
    } else {
      els.hubNudge.textContent = `Add any parts for your ${state.make} ${state.model} — no limit.`;
    }
  }

  if (els.ownedList) {
    if (!owned.length) {
      els.ownedList.innerHTML =
        '<li class="muted">Equip parts to start your garage inventory.</li>';
    } else {
      els.ownedList.innerHTML = owned
        .map((part) => {
          const inBuild = isOwned(part);
          return `<li><strong>${part.name}</strong><span>${inBuild ? "In build" : "Saved"}</span></li>`;
        })
        .join("");
    }
  }
}

function renderSavedBuilds() {
  if (!profile.builds.length) {
    els.savedBuilds.innerHTML = `<li class="muted">No saved builds yet</li>`;
    return;
  }

  els.savedBuilds.innerHTML = profile.builds
    .map((build, index) => {
      const partsCount = Object.keys(normalizeOwned(build.owned || build.equipped || {})).length;
      return `<li>
        <strong>${build.label}</strong>
        <span>${build.vehicle} · ${partsCount} parts · ${money(build.total)} · PERF ${build.perf}</span>
        <button type="button" data-load-build="${index}">Load build</button>
      </li>`;
    })
    .join("");

  els.savedBuilds.querySelectorAll("[data-load-build]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const idx = Number(btn.getAttribute("data-load-build"));
      loadSavedBuild(profile.builds[idx]);
    });
  });
}

function openProfile() {
  els.profileName.value = profile.name || "";
  els.profileEmail.value = profile.email || "";
  els.profileGarage.value = profile.garage || "";
  if (CUSTOMER && els.profileEmail) {
    els.profileEmail.readOnly = true;
  }
  updateProfileFineprint();
  renderSavedBuilds();
  els.profileOverlay.hidden = false;
}

function closeProfile() {
  els.profileOverlay.hidden = true;
}

function currentBuildSnapshot() {
  const vehicle = currentVehicle();
  if (!vehicle || !state.year || !state.make || !state.model) return null;
  const items = manualOwnedParts();
  if (!items.length) return null;
  const total = items.reduce((sum, p) => sum + p.price, 0);
  const perf = 100 + items.reduce((sum, p) => sum + (p.perf || 0), 0);
  return {
    id: Date.now(),
    label: `${profile.garage || "Build"} #${(profile.builds.length || 0) + 1}`,
    vehicle: `${state.year} ${state.make} ${state.model}`,
    year: state.year,
    make: state.make,
    model: state.model,
    trim: state.trim || "",
    engine: state.engine || "",
    owned: { ...state.owned },
    total,
    perf,
  };
}

function loadSavedBuild(build) {
  if (!build) return;
  els.yearSelect.value = String(build.year);
  onYearChange();
  els.makeSelect.value = build.make;
  onMakeChange();
  els.modelSelect.value = build.model;
  onModelChange();
  if (els.trimSelect && build.trim) {
    if ([...els.trimSelect.options].some((o) => o.value === build.trim)) {
      els.trimSelect.value = build.trim;
      onTrimChange();
    }
  }
  if (els.engineSelect && build.engine) {
    if ([...els.engineSelect.options].some((o) => o.value === build.engine)) {
      els.engineSelect.value = build.engine;
      onEngineChange();
    }
  }
  state.owned = normalizeOwned(build.owned || build.equipped || {});
  persistCurrentVehicleBuild();
  renderOwnedRail();
  renderParts();
  renderBuild();
  closeProfile();
  showToast(`Loaded ${build.label}`);
}

function persistLastVehicle() {
  if (!state.year || !state.make || !state.model) return;
  profile.lastVehicle = {
    year: state.year,
    make: state.make,
    model: state.model,
    trim: state.trim || "",
    engine: state.engine || "",
  };
  saveProfile(profile);
}

function buildProgress() {
  const available = partsForSelection(
    state.year,
    state.make,
    state.model,
    state.trim,
    state.engine
  );
  const inBuild = manualOwnedParts();
  const unpurchased = available.filter((part) => !isPurchased(part));
  const missing = unpurchased.filter((part) => !isOwned(part));
  const total = unpurchased.reduce((sum, part) => sum + part.price, 0);
  const missingTotal = missing.reduce((sum, part) => sum + part.price, 0);
  const starter = missing[0] || unpurchased[0] || null;
  return {
    available: unpurchased,
    inBuild,
    missing,
    total,
    missingTotal,
    starter,
  };
}

function bundleQualifies(itemCount) {
  return Boolean(BUNDLE_CODE) && BUNDLE_PERCENT > 0 && itemCount >= BUNDLE_MIN_ITEMS;
}

function bundleSavings(total) {
  return Math.round(total * (BUNDLE_PERCENT / 100) * 100) / 100;
}

function renderBuildNudge() {
  if (!els.buildNudge || LOOKUP_MODE) return;

  const vehicle = currentVehicle();
  if (!vehicle || !state.year) {
    els.buildNudge.hidden = true;
    if (els.buildNudgeFine) els.buildNudgeFine.hidden = true;
    return;
  }

  const progress = buildProgress();
  const filled = progress.inBuild.length;
  const missingCount = progress.missing.length;
  const buildTotal = progress.inBuild.reduce((sum, part) => sum + part.price, 0);
  const qualifies = bundleQualifies(filled);
  const needForBundle = Math.max(0, BUNDLE_MIN_ITEMS - filled);

  /* Empty cart: quiet empty state in the list — no "start your build" promo */
  if (!filled) {
    els.buildNudge.hidden = true;
    if (els.buildNudgeFine) {
      if (BUNDLE_CODE && progress.available.length) {
        els.buildNudgeFine.hidden = false;
        els.buildNudgeFine.textContent = `Add ${BUNDLE_MIN_ITEMS}+ parts to unlock ${BUNDLE_PERCENT}% off with ${BUNDLE_CODE}.`;
      } else {
        els.buildNudgeFine.hidden = true;
      }
    }
    if (els.buyBuild) els.buyBuild.textContent = "Add cart to checkout";
    return;
  }

  if (filled && missingCount) {
    els.buildNudge.hidden = false;
    els.buildNudge.className = "build-nudge build-nudge--progress";
    els.buildNudge.innerHTML = `
      <div class="build-nudge__copy">
        <strong>${filled} in cart</strong>
        <span>${
          qualifies
            ? `${BUNDLE_PERCENT}% off unlocked · save ~${money(bundleSavings(buildTotal))}`
            : `Add ${needForBundle} more for ${BUNDLE_PERCENT}% off`
        }</span>
      </div>
      <button type="button" class="build-nudge__cta" data-scroll-parts>${qualifies ? "Add more" : "Unlock discount"}</button>
    `;
    if (els.buildNudgeFine) {
      els.buildNudgeFine.hidden = false;
      els.buildNudgeFine.textContent = qualifies
        ? `Code ${BUNDLE_CODE} applies when you checkout this cart.`
        : `Add ${needForBundle} more part${needForBundle === 1 ? "" : "s"} to unlock ${BUNDLE_CODE}.`;
    }
    if (els.buyBuild) {
      els.buyBuild.textContent = qualifies
        ? `Checkout ${filled} · ${BUNDLE_PERCENT}% off`
        : `Checkout ${filled} item${filled === 1 ? "" : "s"}`;
    }
  } else {
    els.buildNudge.hidden = false;
    els.buildNudge.className = "build-nudge build-nudge--ready";
    els.buildNudge.innerHTML = `
      <div class="build-nudge__copy">
        <strong>${qualifies ? "Bundle discount unlocked" : "Ready to checkout"}</strong>
        <span>${
          qualifies
            ? `${filled} parts · ${money(buildTotal)} → ~${money(buildTotal - bundleSavings(buildTotal))} with ${BUNDLE_CODE}`
            : `${filled} part${filled === 1 ? "" : "s"} · ${money(buildTotal)}`
        }</span>
      </div>
      <button type="button" class="build-nudge__cta" data-buy-build>${qualifies ? `Checkout ${BUNDLE_PERCENT}% off` : "Checkout now"}</button>
    `;
    if (els.buildNudgeFine) {
      if (qualifies) {
        els.buildNudgeFine.hidden = false;
        els.buildNudgeFine.textContent = `Code ${BUNDLE_CODE} applies automatically at checkout.`;
      } else {
        els.buildNudgeFine.hidden = true;
      }
    }
    if (els.buyBuild) {
      els.buyBuild.textContent = qualifies
        ? `Checkout ${filled} · ${BUNDLE_PERCENT}% off`
        : `Checkout ${filled} item${filled === 1 ? "" : "s"}`;
    }
  }

  els.buildNudge.querySelectorAll("[data-scroll-parts]").forEach((btn) => {
    btn.addEventListener("click", () => {
      els.partsList?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  });
  els.buildNudge.querySelector("[data-buy-build]")?.addEventListener("click", addBuildToCart);
}

function reorderPart(part) {
  if (!part) return;
  addPartToCart(part);
}

function money(n) {
  return `$${n.toFixed(2)}`;
}

function cartUrlForParts(items, options = {}) {
  const lines = items
    .filter((p) => p.variantId)
    .map((p) => `${p.variantId}:1`);
  if (!lines.length) return null;
  const cartPath = `/cart/${lines.join(",")}`;
  const applyBundle = Boolean(options.applyBundle) && bundleQualifies(items.length);
  if (applyBundle && BUNDLE_CODE) {
    return `${STORE_URL}/discount/${encodeURIComponent(BUNDLE_CODE)}?redirect=${encodeURIComponent(cartPath)}`;
  }
  return `${STORE_URL}${cartPath}`;
}

function addBuildToCart() {
  const items = manualOwnedParts().filter((p) => !isPurchased(p));
  if (!items.length) {
    showToast("Add parts to your cart first");
    return;
  }
  const withBundle = bundleQualifies(items.length);
  const cartUrl = cartUrlForParts(items, { applyBundle: withBundle });
  const missing = items.filter((p) => !p.variantId).length;
  if (!cartUrl) {
    showToast("These items can't be added to cart yet");
    return;
  }
  if (withBundle) {
    showToast(`Adding ${items.length} parts with ${BUNDLE_PERCENT}% off (${BUNDLE_CODE})…`);
  } else if (missing) {
    showToast(`Adding ${items.length - missing} item${items.length - missing === 1 ? "" : "s"} to cart…`);
  } else {
    showToast(`Adding ${items.length} item${items.length === 1 ? "" : "s"} to cart…`);
  }
  if (EMBEDDED) {
    window.location.assign(cartUrl);
  } else {
    window.open(cartUrl, "_blank", "noopener");
  }
}

function uniqueSorted(values) {
  return values
    .filter((v, i, arr) => v && arr.indexOf(v) === i)
    .sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true }));
}

function fillSelect(select, values, placeholder, disabled) {
  select.innerHTML = "";
  const opt0 = document.createElement("option");
  opt0.value = "";
  opt0.textContent = placeholder;
  select.appendChild(opt0);
  values.forEach((value) => {
    const opt = document.createElement("option");
    opt.value = String(value);
    opt.textContent = String(value);
    select.appendChild(opt);
  });
  select.disabled = disabled || values.length === 0;
}

function yearsAvailable() {
  const years = [];
  VEHICLES.forEach((v) => {
    for (let y = v.yearEnd; y >= v.yearStart; y--) years.push(y);
  });
  return uniqueSorted(years).sort((a, b) => Number(b) - Number(a));
}

function makesForYear(year) {
  const y = Number(year);
  return uniqueSorted(
    VEHICLES.filter((v) => y >= v.yearStart && y <= v.yearEnd).map((v) => v.make)
  );
}

function modelsForYearMake(year, make) {
  const y = Number(year);
  return uniqueSorted(
    VEHICLES.filter((v) => v.make === make && y >= v.yearStart && y <= v.yearEnd).map(
      (v) => v.model
    )
  );
}

function vehicleOptionRow(year, make, model) {
  const y = Number(year);
  if (!y || !make || !model) return null;
  return (
    VEHICLE_OPTIONS.find(
      (row) =>
        row.make === make &&
        row.model === model &&
        y >= Number(row.yearStart) &&
        y <= Number(row.yearEnd)
    ) || null
  );
}

function trimsForSelection(year, make, model) {
  const row = vehicleOptionRow(year, make, model);
  const trims = row?.trims?.filter(Boolean) || [];
  return trims.length ? uniqueSorted(trims) : ["All submodels"];
}

function enginesForSelection(year, make, model, trim) {
  const row = vehicleOptionRow(year, make, model);
  const engines = row?.engines?.filter(Boolean) || [];
  if (engines.length) return uniqueSorted(engines);
  return ["All engines"];
}

function resetTrimEngineSelects(disabled) {
  if (els.trimSelect) fillSelect(els.trimSelect, [], "Submodel / Trim", true);
  if (els.engineSelect) fillSelect(els.engineSelect, [], "Engine", true);
  state.trim = "";
  state.engine = "";
  if (disabled !== false) {
    /* already disabled via fillSelect */
  }
}

function currentVehicle() {
  if (!state.year || !state.make || !state.model) return null;
  const y = Number(state.year);
  const base = VEHICLES.find(
    (v) =>
      v.make === state.make &&
      v.model === state.model &&
      y >= v.yearStart &&
      y <= v.yearEnd
  );
  if (!base) return null;

  const parts = { strut: [], mirrors: [], shifter: [], paddles: [] };
  partsForSelection(
    state.year,
    state.make,
    state.model,
    state.trim,
    state.engine
  ).forEach((part) => {
    if (parts[part.slot]) parts[part.slot].push(part);
  });

  return {
    ...base,
    trim: state.trim,
    engine: state.engine,
    parts,
  };
}

function showToast(message) {
  if (!els.toast) return;
  els.toast.hidden = false;
  els.toast.textContent = message;
  clearTimeout(showToast._t);
  showToast._t = setTimeout(() => {
    els.toast.hidden = true;
  }, 1600);
}

function allPartsForVehicle(vehicle) {
  if (!vehicle) return [];
  const rows = [];
  Object.keys(SLOT_LABELS).forEach((slot) => {
    (vehicle.parts[slot] || []).forEach((part) => {
      rows.push({ ...part, slot });
    });
  });
  return rows;
}

function renderCatalog() {
  if (!els.catalogList || !els.catalogCount) return;
  const vehicle = currentVehicle();
  const rows = allPartsForVehicle(vehicle);

  els.catalogCount.textContent = `${rows.length} available`;

  if (!vehicle) {
    els.catalogList.innerHTML =
      '<p class="catalog__empty">Select year, make, and model to see available parts.</p>';
    return;
  }

  if (!rows.length) {
    els.catalogList.innerHTML =
      '<p class="catalog__empty">No parts listed for this vehicle yet.</p>';
    return;
  }

  els.catalogList.innerHTML = "";
  rows.forEach((part) => {
    const owned = isOwned(part);
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "catalog-item" + (owned ? " is-equipped" : "");
    btn.innerHTML = `
      <span>
        <span class="catalog-item__slot">${SLOT_LABELS[part.slot]}</span>
        <span class="catalog-item__name">${part.name}</span>
        <span class="catalog-item__meta">${part.meta} · +${part.perf} PERF${owned ? " · Owned" : ""}</span>
      </span>
      <span class="catalog-item__price">${money(part.price)}</span>
    `;
    btn.addEventListener("click", () => {
      state.activeSlot = part.slot;
      renderParts();
      if (owned) unequip(partKey(part));
      else equip(part);
    });
    els.catalogList.appendChild(btn);
  });
}

function renderOwnedPanel() {
  if (!els.ownedPanelList || !els.ownedCount) return;
  const purchased = Object.values(state.purchased);
  const manual = manualOwnedParts();
  const all = garageParts();
  const purchasedCount = purchased.length;

  if (CUSTOMER) {
    els.ownedCount.textContent =
      purchasedCount === 1 ? "1 purchased" : `${purchasedCount} purchased`;
  } else {
    els.ownedCount.textContent = `${all.length} in build`;
  }

  if (!all.length) {
    els.ownedPanelList.innerHTML = CUSTOMER
      ? '<li class="muted">No Brace1 parts on this account yet — buy from our store and they will show here.</li>'
      : '<li class="muted"><a href="' +
        (CUSTOMER?.loginUrl || "/account/login") +
        '">Sign in</a> to see parts you\'ve purchased, or equip parts to plan a build.</li>';
    return;
  }

  els.ownedPanelList.innerHTML = groupPartsByVehicle(all)
    .map((group) => {
      const items = group.parts
        .map((part) => {
          const bought = isPurchased(part);
          const tag = bought ? "Purchased" : "In build";
          const reorderBtn = bought
            ? `<button type="button" class="owned-panel__reorder" data-reorder-id="${partKey(part)}">Reorder</button>`
            : "";
          return `<li class="owned-panel__item${bought ? " is-purchased" : ""}">
            <div class="owned-panel__item-copy">
              <strong>${part.name}</strong>
              <span>${SLOT_LABELS[part.slot] || "Part"} · ${tag}</span>
            </div>
            ${reorderBtn}
          </li>`;
        })
        .join("");
      return `
        <li class="owned-panel__vehicle${isActiveVehicleGroup(group) ? " is-active" : ""}">
          <button class="owned-panel__vehicle-head" type="button" data-vehicle-group="${vehicleGroupKey(group.make, group.model) || "other"}">
            <strong>${vehicleGroupLabel(group)}</strong>
            <span>${vehicleGroupMeta(group)}</span>
          </button>
          <ul class="owned-panel__vehicle-parts">${items}</ul>
        </li>`;
    })
    .join("");

  els.ownedPanelList.querySelectorAll("[data-vehicle-group]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const key = btn.dataset.vehicleGroup;
      const group = groupPartsByVehicle(all).find(
        (row) =>
          (row.unassigned && key === "other") ||
          vehicleGroupKey(row.make, row.model) === key
      );
      if (group) selectVehicleGroup(group);
    });
  });

  els.ownedPanelList.querySelectorAll("[data-reorder-id]").forEach((btn) => {
    btn.addEventListener("click", (event) => {
      event.stopPropagation();
      const part = all.find((row) => partKey(row) === btn.dataset.reorderId);
      reorderPart(part);
    });
  });
}

function addPartToCart(part) {
  const cartUrl = cartUrlForParts([part]);
  if (!cartUrl) {
    showToast("This item can't be added to cart yet");
    return;
  }
  showToast("Adding to cart…");
  window.location.assign(cartUrl);
}

function garageUrlForVehicle() {
  if (!state.year || !state.make || !state.model) return "/pages/garage";
  const params = new URLSearchParams({
    year: state.year,
    make: state.make,
    model: state.model,
  });
  return `/pages/garage?${params.toString()}`;
}

function updateLookupGarageLinks() {
  const url = garageUrlForVehicle();
  if (els.lookupGarageLink) els.lookupGarageLink.href = url;
  if (els.lookupBuildGarageLink) els.lookupBuildGarageLink.href = url;
}

function renderLookupBuild() {
  if (!LOOKUP_MODE || !els.lookupBuildBar) return;

  const items = manualOwnedParts();
  const total = items.reduce((sum, p) => sum + Number(p.price || 0), 0);
  const filled = items.length;

  if (els.lookupBuildCount) {
    els.lookupBuildCount.textContent =
      filled === 1 ? "1 part in your build" : `${filled} parts in your build`;
  }
  if (els.lookupBuildTotal) els.lookupBuildTotal.textContent = money(total);

  if (!filled) {
    els.lookupBuildBar.hidden = true;
    if (els.lookupBuyBuild) els.lookupBuyBuild.disabled = true;
    return;
  }

  els.lookupBuildBar.hidden = false;
  if (els.lookupBuyBuild) {
    els.lookupBuyBuild.disabled = false;
    els.lookupBuyBuild.textContent = bundleQualifies(filled)
      ? `Add build to cart · ${BUNDLE_CODE} ${BUNDLE_PERCENT}% off`
      : "Add build to cart";
  }
}

function renderParts() {
  if (!els.partsList) return;

  const vehicle = currentVehicle();
  const parts = allPartsForVehicle(vehicle);

  if (els.panelEyebrow) els.panelEyebrow.textContent = "AVAILABLE PARTS";
  if (els.panelTitle) {
    els.panelTitle.textContent = vehicle
      ? `${state.year} ${vehicle.make} ${vehicle.model}`
      : "Select a vehicle";
  }

  if (!vehicle) {
    if (els.panelHint) {
      els.panelHint.textContent = "Choose year, make, and model to see every part that fits.";
    }
    els.partsList.innerHTML =
      '<p class="panel__hint">Select your vehicle above to load available parts.</p>';
    renderOwnedPanel();
    return;
  }

  if (els.panelHint) {
    els.panelHint.textContent = `${parts.length} part${
      parts.length === 1 ? "" : "s"
    } available — click a part to add, or View part for details.`;
  }
  els.partsList.innerHTML = "";

  parts.forEach((part) => {
    const selected = isOwned(part);
    const bought = isPurchased(part);
    const productUrl = part.url || "";
    /* article so View part <a> is valid (not nested in a button) */
    const card = document.createElement("article");
    card.className = "part" + (selected ? " is-selected" : "") + (bought ? " is-purchased" : "");
    card.setAttribute("role", "group");
    card.setAttribute("aria-label", part.name);
    /* Shared attribute used by Garage tuning category filters (lookup-safe). */
    if (part.slot) card.setAttribute("data-slot", part.slot);
    if (part.category) card.setAttribute("data-category", part.category);
    const thumb = part.image
      ? `<img class="part__img" src="${part.image}" alt="" loading="lazy" />`
      : `<span class="part__img part__img--empty">${SLOT_LABELS[part.slot]?.[0] || "P"}</span>`;
    const action = bought
      ? "Purchased — use Reorder below"
      : selected
        ? "In cart — click again to remove"
        : "Click to add to cart";
    const viewPartHtml = productUrl
      ? `<a class="part__link part__link--ghost" href="${productUrl}" data-view-part>View part</a>`
      : "";
    const addBtnHtml = bought
      ? ""
      : selected
        ? `<button class="part__cart-btn part__cart-btn--in" type="button" data-toggle-cart>Remove</button>`
        : `<button class="part__cart-btn" type="button" data-toggle-cart>Add to cart</button>`;
    /* Image is display-only — View part is the open-product control */
    card.innerHTML = `
      ${thumb}
      <span class="part__body">
        <span class="part__name">${part.name}</span>
        <span class="part__meta">${SLOT_LABELS[part.slot]} · ${part.meta} · +${part.perf} PERF</span>
        <span class="part__action">${action}</span>
      </span>
      <span class="part__aside">
        <span class="part__price">${money(part.price)}</span>
        <span class="part__aside-actions">
          ${viewPartHtml}
          ${addBtnHtml}
          ${
            bought
              ? `<button class="part__reorder" type="button" data-reorder>Reorder</button>`
              : ""
          }
        </span>
      </span>
    `;
    function toggleCartPart() {
      state.activeSlot = part.slot;
      if (isPurchased(part)) return;
      const key = partKey(part);
      const equipped = Boolean(key && state.owned[key]);
      if (equipped) unequip(key);
      else equip({ ...part, slot: part.slot });
    }
    card.style.cursor = bought ? "default" : "pointer";
    card.addEventListener("click", (event) => {
      /* View part / any link — do not add to cart */
      if (event.target.closest("a, [data-view-part]")) return;
      if (event.target.closest("[data-reorder]")) {
        event.preventDefault();
        reorderPart(part);
        return;
      }
      if (event.target.closest("[data-toggle-cart]")) {
        event.preventDefault();
        toggleCartPart();
        return;
      }
      /* Clicking the part card adds/removes from cart */
      if (!bought) toggleCartPart();
    });
    els.partsList.appendChild(card);
  });

  renderOwnedPanel();
  if (LOOKUP_MODE) renderLookupBuild();
}

function renderOwnedRail() {
  if (!els.slotRail) return;
  const all = garageParts();
  els.slotRail.innerHTML = "";

  if (!all.length) {
    els.slotRail.innerHTML = CUSTOMER
      ? '<p class="slot-rail__empty">Parts you buy from Brace1 will appear here — grouped by car.</p>'
      : '<p class="slot-rail__empty">Equipped parts will appear here. <a href="/account/login">Sign in</a> to sync purchases.</p>';
    return;
  }

  const groups = groupPartsByVehicle(all);
  const useGroupedLayout = groups.length > 1 || (groups[0] && !groups[0].unassigned);

  if (!useGroupedLayout) {
    all.forEach((part, index) => {
      els.slotRail.appendChild(createOwnedSlot(part, index));
    });
    return;
  }

  const wrap = document.createElement("div");
  wrap.className = "garage-cars";

  groups.forEach((group) => {
    const section = document.createElement("section");
    section.className =
      "garage-car" + (isActiveVehicleGroup(group) ? " is-active" : "");

    const head = document.createElement("button");
    head.type = "button";
    head.className = "garage-car__head";
    head.innerHTML = `
      <span class="garage-car__name">${vehicleGroupLabel(group)}</span>
      <span class="garage-car__meta">${vehicleGroupMeta(group)}</span>
    `;
    if (!group.unassigned) {
      head.addEventListener("click", () => selectVehicleGroup(group));
    }

    const rail = document.createElement("div");
    rail.className = "garage-car__rail slot-rail";
    group.parts.forEach((part, index) => {
      rail.appendChild(createOwnedSlot(part, index));
    });

    section.appendChild(head);
    section.appendChild(rail);
    wrap.appendChild(section);
  });

  els.slotRail.appendChild(wrap);
}

function playEquipFx() {
  if (!els.stage) return;
  els.stage.classList.remove("is-equipping");
  void els.stage.offsetWidth;
  els.stage.classList.add("is-equipping");
  if (els.perfScore) {
    els.perfScore.classList.remove("perf-bump");
    void els.perfScore.offsetWidth;
    els.perfScore.classList.add("perf-bump");
  }
}

function equip(part) {
  const key = partKey(part);
  if (!key) return;
  if (isPurchased(part)) {
    showToast("Already in your garage — purchased");
    return;
  }
  if (state.owned[key]) {
    showToast("Already in your cart");
    return;
  }
  state.owned[key] = part;
  persistCurrentVehicleBuild();
  playEquipFx();
  renderOwnedRail();
  renderParts();
  renderBuild();
  showToast(`Added: ${part.name}`);
}

function unequip(id) {
  const key = String(id);
  if (state.purchased[key]) {
    showToast("Purchased parts stay in your garage");
    return;
  }
  const part = state.owned[key];
  delete state.owned[key];
  persistCurrentVehicleBuild();
  renderOwnedRail();
  renderParts();
  renderBuild();
  showToast(part ? `Removed: ${part.name}` : "Part removed");
}

function renderBuild() {
  if (LOOKUP_MODE) {
    renderLookupBuild();
    return;
  }
  if (!els.buildCount || !els.buildFill || !els.buildTotal || !els.perfScore || !els.buyBuild || !els.buildList) {
    return;
  }
  const items = manualOwnedParts();
  const purchased = purchasedForSelection();
  const currentGarage = currentVehicleGarageParts();
  const total = items.reduce((sum, p) => sum + Number(p.price || 0), 0);
  const perf = 100 + currentGarage.reduce((sum, p) => sum + (p.perf || 0), 0);
  const filled = items.length;
  const available = partsForSelection(
    state.year,
    state.make,
    state.model,
    state.trim,
    state.engine
  ).length;

  const ownedEl = byId("ownedCount");
  if (ownedEl) {
    ownedEl.textContent = `${filled} item${filled === 1 ? "" : "s"}`;
  }
  if (els.buildCount) els.buildCount.textContent = String(currentGarage.length);
  if (els.buildFill) {
    els.buildFill.style.width = `${available ? Math.min(100, (currentGarage.length / available) * 100) : 0}%`;
  }
  els.buildTotal.textContent = money(total);
  if (els.perfScore) els.perfScore.textContent = String(perf);
  els.buyBuild.disabled = filled === 0;

  if (!filled && !purchased.length) {
    els.buildList.innerHTML = `<li class="build-card__empty muted">Your cart is empty. Click <strong>Add to cart</strong> on a part — it shows up here.</li>`;
    renderBuildNudge();
    return;
  }

  if (!filled) {
    els.buildList.innerHTML = `<li class="build-card__empty muted">${purchased.length} purchased part${purchased.length === 1 ? "" : "s"} owned in garage. Add more below to fill this cart.</li>`;
    renderBuildNudge();
    return;
  }

  els.buildList.innerHTML = items
    .map((part) => {
      const key = partKey(part);
      const img = part.image
        ? `<img class="build-line__img" src="${part.image}" alt="" loading="lazy" width="48" height="48" />`
        : `<span class="build-line__img build-line__img--empty" aria-hidden="true"></span>`;
      const view = part.url
        ? `<a class="build-line__view" href="${part.url}">View</a>`
        : "";
      return `<li class="build-line" data-part-key="${key}">
        ${img}
        <span class="build-line__body">
          <span class="build-line__name">${part.name}</span>
          <span class="build-line__meta">${SLOT_LABELS[part.slot] || ""} · ${money(part.price)}</span>
          <span class="build-line__actions">
            ${view}
            <button type="button" class="build-line__remove" data-remove="${key}">Remove</button>
          </span>
        </span>
        <strong class="build-line__price">${money(part.price)}</strong>
      </li>`;
    })
    .join("");

  els.buildList.querySelectorAll("[data-remove]").forEach((btn) => {
    btn.addEventListener("click", () => unequip(btn.getAttribute("data-remove")));
  });
  renderBuildNudge();
}

function applyVehicle() {
  const vehicle = currentVehicle();

  if (!vehicle) {
    state.owned = {};
    if (els.stageTitle) els.stageTitle.textContent = "Select your vehicle";
    if (els.bayCode) els.bayCode.textContent = "B1-READY";
    if (els.lookupStageTitle) els.lookupStageTitle.textContent = "Select a vehicle";
    if (els.lookupBayCode) els.lookupBayCode.textContent = "B1-READY";
    renderOwnedRail();
    renderParts();
    renderBuild();
    updateLookupGarageLinks();
    return;
  }

  loadCurrentVehicleBuild();

  if (els.stageTitle) els.stageTitle.textContent = vehicle.title;
  if (els.bayCode) els.bayCode.textContent = vehicle.code;
  if (els.lookupStageTitle) {
    els.lookupStageTitle.textContent = `Build your ${vehicle.model}`;
  }
  if (els.lookupBayCode) els.lookupBayCode.textContent = vehicle.code;
  persistLastVehicle();
  renderOwnedRail();
  renderParts();
  renderBuild();
  updateLookupGarageLinks();
  if (!LOOKUP_MODE) {
    const bits = [state.year, state.make, state.model];
    if (state.trim && state.trim !== "All submodels") bits.push(state.trim);
    if (state.engine && state.engine !== "All engines") bits.push(state.engine);
    showToast(`Loaded ${bits.join(" ")}`);
  }
}

function updateProfileFineprint() {
  const el = byId("profileFineprint");
  if (!el) return;
  if (CUSTOMER) {
    const count = Object.keys(state.purchased).length;
    el.textContent =
      count === 1
        ? "Signed in — 1 Brace1 part synced from your orders."
        : `Signed in — ${count} Brace1 parts synced from your orders.`;
  } else {
    el.innerHTML =
      'Sign in to sync parts you\'ve purchased. <a href="/account/login">Log in</a> or <a href="/account/register">create an account</a>.';
  }
}

function updateLookupWelcome() {
  if (!LOOKUP_MODE) return;
  const sub = byId("lookupWelcomeSub");
  if (!sub || !CUSTOMER) return;
  const count = Object.keys(state.purchased).length;
  if (count === 1) {
    sub.textContent = "1 Brace1 part synced — select your car to see what you own.";
  } else if (count > 1) {
    sub.textContent = `${count} Brace1 parts synced — select your car to see what you own.`;
  }
}

function onYearChange() {
  persistCurrentVehicleBuild();
  state.year = els.yearSelect.value;
  state.make = "";
  state.model = "";
  resetTrimEngineSelects();
  fillSelect(els.modelSelect, [], "Model", true);
  if (!state.year) {
    fillSelect(els.makeSelect, [], "Make", true);
  } else {
    fillSelect(els.makeSelect, makesForYear(state.year), "Make", false);
  }
  applyVehicle();
}

function onMakeChange() {
  persistCurrentVehicleBuild();
  state.make = els.makeSelect.value;
  state.model = "";
  resetTrimEngineSelects();
  if (!state.year || !state.make) {
    fillSelect(els.modelSelect, [], "Model", true);
  } else {
    fillSelect(els.modelSelect, modelsForYearMake(state.year, state.make), "Model", false);
  }
  applyVehicle();
}

function onModelChange() {
  persistCurrentVehicleBuild();
  state.model = els.modelSelect.value;
  state.trim = "";
  state.engine = "";
  if (els.engineSelect) fillSelect(els.engineSelect, [], "Engine", true);

  if (!state.year || !state.make || !state.model) {
    if (els.trimSelect) fillSelect(els.trimSelect, [], "Submodel / Trim", true);
    applyVehicle();
    return;
  }

  const trims = trimsForSelection(state.year, state.make, state.model);
  if (els.trimSelect) {
    fillSelect(els.trimSelect, trims, "Submodel / Trim", false);
  }
  applyVehicle();
}

function onTrimChange() {
  persistCurrentVehicleBuild();
  state.trim = els.trimSelect ? els.trimSelect.value : "";
  state.engine = "";
  if (!state.trim) {
    if (els.engineSelect) fillSelect(els.engineSelect, [], "Engine", true);
    applyVehicle();
    return;
  }
  const engines = enginesForSelection(state.year, state.make, state.model, state.trim);
  if (els.engineSelect) {
    fillSelect(els.engineSelect, engines, "Engine", false);
  }
  applyVehicle();
}

function onEngineChange() {
  persistCurrentVehicleBuild();
  state.engine = els.engineSelect ? els.engineSelect.value : "";
  applyVehicle();
}

function showPostPurchaseBanner() {
  if (LOOKUP_MODE) return;
  const banner = byId("garagePostPurchase");
  if (!banner) return;

  const params = new URLSearchParams(window.location.search);
  if (params.get("from") !== "order" && params.get("purchase") !== "1") return;

  banner.hidden = false;
  byId("garagePostPurchaseCta")?.addEventListener("click", () => {
    els.partsList?.scrollIntoView({ behavior: "smooth", block: "start" });
  });

  if (CUSTOMER && Object.keys(state.purchased).length) {
    showToast("Purchase synced — finish your build");
  }
}

if (garageRoot) {
  if (els.buyBuild) els.buyBuild.addEventListener("click", addBuildToCart);
  if (els.lookupBuyBuild) els.lookupBuyBuild.addEventListener("click", addBuildToCart);

  if (els.clearBuild) {
    els.clearBuild.addEventListener("click", () => {
      state.owned = {};
      persistCurrentVehicleBuild();
      renderOwnedRail();
      renderParts();
      renderBuild();
      showToast("Cart cleared for this car");
    });
  }

  if (els.yearSelect) els.yearSelect.addEventListener("change", onYearChange);
  if (els.makeSelect) els.makeSelect.addEventListener("change", onMakeChange);
  if (els.modelSelect) els.modelSelect.addEventListener("change", onModelChange);
  if (els.trimSelect) els.trimSelect.addEventListener("change", onTrimChange);
  if (els.engineSelect) els.engineSelect.addEventListener("change", onEngineChange);

  if (els.profileOpen) els.profileOpen.addEventListener("click", openProfile);
  if (els.profileClose) els.profileClose.addEventListener("click", closeProfile);
  if (els.profileOverlay) {
    els.profileOverlay.addEventListener("click", (e) => {
      if (e.target === els.profileOverlay) closeProfile();
    });
  }

  if (els.profileForm) {
    els.profileForm.addEventListener("submit", (e) => {
      e.preventDefault();
      profile.name = els.profileName.value.trim() || "Driver";
      profile.email = els.profileEmail.value.trim();
      profile.garage = els.profileGarage.value.trim() || "My Garage";
      touchProfile();
      renderProfileChip();
      showToast("Profile saved");
      closeProfile();
    });
  }

  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && els.profileOverlay && !els.profileOverlay.hidden) closeProfile();
  });

  if (els.saveBuildBtn) {
    els.saveBuildBtn.addEventListener("click", () => {
      const snap = currentBuildSnapshot();
      if (!snap) {
        showToast("Select a vehicle and equip parts first");
        return;
      }
      profile.builds = [snap, ...(profile.builds || [])].slice(0, 8);
      touchProfile();
      renderSavedBuilds();
      showToast("Build saved to your profile");
    });
  }

  renderProfileChip();
  boot();
}

async function boot() {
  try {
    await loadCatalog();
  } catch (err) {
    showToast("Could not load Brace1 catalog");
    console.error(err);
    return;
  }

  syncProfileFromCustomer();
  mergeCloudGarage();
  applyPurchasedFromAccount();
  renderProfileChip();
  updateProfileFineprint();
  updateLookupWelcome();
  showPostPurchaseBanner();

  if (CUSTOMER && Object.keys(state.purchased).length && !LOOKUP_MODE && !ACCOUNT_MODE) {
    showToast(
      `${Object.keys(state.purchased).length} purchased part${Object.keys(state.purchased).length === 1 ? "" : "s"} in your garage`
    );
  }

  if (ACCOUNT_MODE) {
    renderOwnedRail();
    const countEl = byId("accountOwnedCount");
    const parts = garageParts();
    const groups = groupPartsByVehicle(parts).filter((g) => !g.unassigned);
    if (countEl) {
      const n = parts.length;
      countEl.textContent = `${n} part${n === 1 ? "" : "s"} in your garage`;
    }
    const partsOwned = byId("baPartsOwned");
    const vehOwned = byId("baVehiclesOwned");
    const vehPlural = byId("baVehiclesPlural");
    const garageEmpty = byId("baGarageEmpty");
    if (partsOwned) partsOwned.textContent = String(parts.length);
    if (vehOwned) {
      const v = groups.length || (parts.length ? 1 : 0);
      vehOwned.textContent = String(v);
      if (vehPlural) vehPlural.textContent = v === 1 ? "" : "s";
    }
    if (garageEmpty) garageEmpty.hidden = parts.length > 0;
    window.BRACE1_GARAGE_API = {
      garageParts: () => garageParts(),
      vehicleGroups: () => groupPartsByVehicle(garageParts()),
    };
    return;
  }

  if (!els.yearSelect) return;

  fillSelect(els.yearSelect, yearsAvailable(), "Year", false);
  fillSelect(els.makeSelect, [], "Make", true);
  fillSelect(els.modelSelect, [], "Model", true);
  if (els.trimSelect) fillSelect(els.trimSelect, [], "Submodel / Trim", true);
  if (els.engineSelect) fillSelect(els.engineSelect, [], "Engine", true);

  if (LOOKUP_MODE) {
    updateLookupGarageLinks();
    renderParts();
    return;
  }

  if (CUSTOMER && CLOUD_SYNC_ENABLED) {
    scheduleCloudSync();
  }

  const bootParams = new URLSearchParams(window.location.search);
  const saved = profile.lastVehicle || {};
  const preferYear = bootParams.get("year") || saved.year || "2019";
  const preferMake = bootParams.get("make") || saved.make || "Honda";
  const preferModel = bootParams.get("model") || saved.model || "Accord";
  const preferTrim = bootParams.get("trim") || saved.trim || "";
  const preferEngine = bootParams.get("engine") || saved.engine || "";
  if (yearsAvailable().includes(Number(preferYear)) || yearsAvailable().includes(preferYear)) {
    els.yearSelect.value = preferYear;
    onYearChange();
    if ([...els.makeSelect.options].some((o) => o.value === preferMake)) {
      els.makeSelect.value = preferMake;
      onMakeChange();
      if ([...els.modelSelect.options].some((o) => o.value === preferModel)) {
        els.modelSelect.value = preferModel;
        onModelChange();
        if (els.trimSelect && preferTrim) {
          if ([...els.trimSelect.options].some((o) => o.value === preferTrim)) {
            els.trimSelect.value = preferTrim;
            onTrimChange();
          }
        }
        if (els.engineSelect && preferEngine) {
          if ([...els.engineSelect.options].some((o) => o.value === preferEngine)) {
            els.engineSelect.value = preferEngine;
            onEngineChange();
          }
        }
      }
    }
  } else if (yearsAvailable().length) {
    els.yearSelect.value = String(yearsAvailable()[0]);
    onYearChange();
  }
}
